/**
 * SPA boot orchestrator for the cookie-auth deployment per
 * [Bifrost #87](https://github.com/Volley-Inc/bifrost/pull/87).
 *
 * The canonical (and only) Foundry SPA boot path. Replaced the BG-3
 * `auth/spa-boot.ts` PKCE orchestrator when cookie-mode shipped to
 * production; the legacy stack was removed in the
 * `chore(crucible-web): remove legacy PKCE auth stack` change. The
 * cookie-mode shape is substantially smaller because there is no:
 *
 *   - Handoff drain (no PKCE callback handler → no token bundle to
 *     stash in sessionStorage)
 *   - Auth Web Worker (no refresh token to protect from XSS — the
 *     ALB session cookie is HttpOnly and the SPA never holds an
 *     access token)
 *   - Token-bundle handshake (the SPA never sees tokens at all)
 *
 * Boot sequence:
 *
 *   1. **Probe** `/crucible/me`. Returns `signed-in`, `signed-out`,
 *      or `error`. See `me-probe.ts`.
 *   2. **signed-out** → phase `'signed-out'`. SPA renders the
 *      signed-out shell with a "Sign in" button that calls
 *      {@link import('./login.js').beginLogin}.
 *   3. **error** → phase `'connect-error'` with the reason
 *      surfaced.
 *   4. **signed-in** → construct `BrowserCrucibleClient` in cookie-
 *      mode (no `tenantHost`, no `getAccessToken`, `capPrefix:
 *      '/crucible/'`) and `connect()` it. Success → phase
 *      `'ready'`; failure → phase `'connect-error'`.
 *
 * Returns `{ phase, client, capabilities, user, errorReason }`;
 * `main.tsx` calls `useCookieBoot()` directly with no further
 * dispatch.
 */

import { useEffect, useRef, useState } from 'react';

import {
  BrowserCrucibleClient,
  type BrowserCrucibleClientOptions,
} from '@volley/crucible-client/BrowserCrucibleClient.js';
import type { ClientCapabilities } from '@volley/crucible-client/CrucibleClient.js';

import { installWindowCrucibleShim } from '../window-crucible-shim.js';
import { ELECTRON_ONLY_STUBS } from './electron-only-stubs.js';
import type { HttpAuthMode } from './http-auth-types.js';
import { AUTH_BROADCAST_CHANNEL, type AuthClearedMessage } from './logout.js';
import {
  defaultProbeDependencies,
  probeIdentity,
  type ProbeDependencies,
  type ProbeResult,
} from './me-probe.js';
import { persistSessionId, readStoredSessionId } from './session-id-store.js';

export type CookieBootPhase = 'idle' | 'probing' | 'ready' | 'connect-error' | 'signed-out';

interface CookieBootUser {
  readonly userId: string;
  readonly authMode: HttpAuthMode;
}

export interface CookieBootState {
  phase: CookieBootPhase;
  /** Active client when phase === 'ready'; null otherwise. */
  client: BrowserCrucibleClient | null;
  /** Capability matrix when phase === 'ready'; null otherwise. */
  capabilities: ClientCapabilities | null;
  /** Signed-in user identity. Non-null when phase === 'ready'. */
  user: CookieBootUser | null;
  /** Human-readable error string when phase === 'connect-error'. */
  errorReason?: string;
}

/**
 * The capability prefix the cookie-auth deployment uses. Hardcoded
 * here because the colocation infra fixes `/crucible/*` as the
 * ALB-routed prefix (see the v2 design doc + v3 follow-up review).
 * If a future infra revision moves the prefix, change this constant.
 */
const CRUCIBLE_CAP_PREFIX = '/crucible/';

/**
 * Channels that have graduated to a runtime handler and must route
 * over the wire in cookie-mode instead of returning the injected
 * `ELECTRON_ONLY_STUBS` default (from `./electron-only-stubs.ts`,
 * passed to the client as `stubChannels`) (lane A per
 * `docs/cloud-vm/design/game-management-methods.md` §5.1 / §5.2).
 *
 * `app:get-feature-flags` (lane A 1a): the static stub returns
 * `cloudVmV1: false` and has no per-user cohort identity. The
 * graduated runtime method returns the real `cloudVmV1: true` + the
 * per-user cohort overlay resolved from `ctx.userId`. It keeps
 * `lazyDashboardLoad` at today's value — the flip to the eager
 * `game.list@1` path lands with that handler PR (Cole-gated,
 * bifrost#95) — so routing this channel to the runtime is
 * behaviour-preserving for the Dashboard's list path today.
 *
 * `app:get-furnace-status` (lane A 1b, #1296): the static stub
 * returns `cloudVmV1Enabled: false` + `cohort: 'unknown'`. The
 * graduated runtime method (`app.get-furnace-status@1`, object
 * contract `FurnaceStatus`, identity = `ctx.userId`, cohort-gated the
 * SAME way the feature-flags overlay is) returns the real
 * `cloudVmV1Enabled` + cohort tri-state. Routing BOTH channels to the
 * runtime is the load-bearing fix (Codex r2 P2): `Workspace` derives
 * Furnace routing from furnace-status, so a cookie-mode user otherwise
 * sees a contradiction — `cloudVmV1: true` from feature-flags but
 * `cloudVmV1Enabled: false` from the still-stubbed furnace-status.
 * Now both read the real cohort, so they agree by construction.
 *
 * `game:create` (lane A 1c, #1314): the "+ New Game" flow. The cloud
 * runtime's `game.create@1` is a STREAMING scaffold + draft-registry
 * write (#1312). Routing the channel here makes
 * `BrowserCrucibleClient.invoke('game:create', …)` take its streaming
 * bridge — it consumes the `game.create.chunk@1` stream over
 * `invokeStreaming` + fans each chunk out to the `game:create:progress`
 * push subscribers the SPA already registers, and resolves the terminal
 * `CreateGameResult`. Without this set membership the channel falls
 * through to its rejecting injected `ELECTRON_ONLY_STUBS` stub (the
 * PKCE / browser-dev fallback), so flag-OFF behaviour is unchanged.
 *
 * `app:get-vm-diagnostics` / `app:get-diagnostics` (lane A 1b, #1296):
 * the VM Debug pane reads. `VmDiagnostics | null` still needs a wire-shape
 * adapter because top-level `null` is not a success result, and
 * `app:get-diagnostics` keeps its historical `{ diagnostics }` envelope.
 * `BrowserCrucibleClient` maps the flag-OFF
 * `app.get-vm-diagnostics@1` shape (`routingDecision: 'local'`) → `null`
 * for the `CapCohortStatePanel` placeholder, and unwraps
 * `app.get-diagnostics@1`'s `{ diagnostics }` → `string` for the Settings
 * secret-scan. Without this set membership both channels fall through to
 * their injected `ELECTRON_ONLY_STUBS` stubs (the browser-dev / PKCE
 * fallback — `null` and a descriptive string respectively), so flag-OFF
 * behaviour is unchanged.
 *
 * `agent:send` (#1777): the Workspace chat turn. The cloud runtime's
 * `agent.send@1` is a STREAMING method (request-correlated
 * `agent.send.chunk@1` notifications, then a terminal result), but the
 * Workspace renders chat from `agent:stream` push events. Routing the
 * channel here makes `BrowserCrucibleClient.invoke('agent:send', …)`
 * take its streaming bridge — it consumes the chunk stream over
 * `invokeStreaming`, translates each originator chunk into the
 * `agent:stream` push payload, fans it out to the subscribers the
 * Workspace already registers, and resolves on the terminal result.
 * Pre-#1777 the channel reached the wire only by ABSENCE of a stub —
 * a bare invoke that swallowed the correlated chunks, so cloud-mode
 * chat hung at "working…" forever while the turn completed
 * server-side. `agent:send` has no `ELECTRON_ONLY_STUBS` entry, so
 * PKCE / browser-dev builds keep the bare-request wire behaviour via
 * the absence of this set membership (unchanged).
 *
 * NB: `app:get-feature-flags` / `app:get-furnace-status` are
 * OBJECT-contract reads served verbatim (no adapter).
 *
 * Design parity: these channels have browser fallback stubs or no local
 * filesystem implementation, but cookie-mode clouds can serve them from the
 * pod workspace once the runtime advertises `designTab`. Listing them here
 * lets the real runtime handlers win over stubs for Design CRUD, build handoff,
 * and reverse Build→Design reconcile.
 *
 * Scoped to the cookie-mode build only — PKCE / browser-dev builds
 * (no runtime behind them) keep the stub via the absence of this set.
 */
export const COOKIE_MODE_RUNTIME_SERVED_CHANNELS = new Set([
  // #3005 — cloud state surfaces now served by real furnace handlers
  // (`game-state.*@1` / `phase-progress.*@1` in
  // `crucible/src/runtime/jsonrpc-methods.ts`). They carry NO stub, so they
  // already reached the wire via the invoke "else forward" path; listing them
  // here makes the forward EXPLICIT and brings them under the #3005 channel↔
  // method contract test (they must map to a registered handler).
  'game-state:get',
  'game-state:subscribe',
  'game-state:unsubscribe',
  'phase-progress:subscribe',
  'phase-progress:unsubscribe',
  'app:get-feature-flags',
  'settings:get-resolved',
  'app:get-furnace-status',
  'app:get-vm-diagnostics',
  'app:get-diagnostics',
  'game:create',
  'game:status',
  'agent:send',
  // Adaptive create intake is owned by the authenticated Cloud runtime. Every
  // request carries an explicit gameId so the pod can enforce registry/create-
  // owner authority; visual recovery uses the channel-router override to hit
  // the runtime's canonical `intake.visuals@1` method.
  'intake:begin',
  'intake:recover',
  'intake:turn',
  'intake:answer',
  'intake:command',
  'intake:skip',
  'intake:visual:current',
  // GAP-1 (docs/cloud-vm/gui-parity-plan.md, Phase 1 — `agent.get-status@1`):
  // the Workspace's `loadChatHistoryWithAgentStatus` invokes this right after
  // `chat:load` to re-attach an in-flight turn. The in-pod runtime now serves
  // `agent.get-status@1` (`crucible/src/runtime/jsonrpc-methods.ts`); without
  // this set membership the channel falls through to the `ELECTRON_ONLY_STUBS`
  // stub and the renderer always took its `.catch(() => ({ inFlight: false }))`
  // fallback → live turns never re-attached and the initial-prompt auto-send
  // could fire into an already-active turn. `channelToMethod` maps
  // `agent:get-status` → `agent.get-status@1`. Scoped to the cookie-mode build
  // only — PKCE / browser-dev keep the stub via the absence of this set.
  'agent:get-status',
  'interview:get-pending-interactions',
  'interview:recover',
  // 3c (docs/cloud-vm/gui-parity-plan.md + agent-activity-journal-tdd.md §6):
  // the Workspace hydration loader reads the durable agent journal so a cloud
  // build survives a full browser reload. `chat:load` is NOT runtime-served in
  // cookie mode (chat-history.json is the desktop IPC path), so `agent.history@1`
  // — the in-pod journal reader (`crucible/src/runtime/jsonrpc-methods.ts`) — is
  // the canonical cloud hydration source; its events project into the chat via
  // `projectAgentHistoryToChatMessages`. `channelToMethod` maps
  // `agent:history` → `agent.history@1`. Returns an empty page on a pre-journal
  // game (graceful — never throws).
  'agent:history',
  'publish:run',
  'build-game:get-interview-state',
  'build-game:interview-status',
  'conflicts:get-pending',
  // crucible#3007 — `art:active`'s cloud result is wrapped in a one-field
  // `{ active }` envelope (a bare top-level `null` is not a valid JSON-RPC
  // success result); listing it here routes `BrowserCrucibleClient.invoke`
  // through the unwrapping adapter (`invokeArtActive`) instead of the plain
  // forward. The other six `art:*` review/steering channels
  // (snapshot/approve/regenerate/swap-take/command/finish) return ordinary
  // object results and need no adapter, so they are NOT listed here — they
  // already reach the wire via the default forward, same as
  // `art:open`/`art:library`/`art:redo`/`art:export` before them.
  'art:active',
  // Crucible #1945 — `dev:start` is a STREAMING runtime method
  // (`dev.start@1` → request-correlated `dev.start.chunk@1`). Routing it
  // here makes `BrowserCrucibleClient.invoke('dev:start')` take the
  // streaming bridge that resolves the `CloudPreviewDevSession` from the
  // first `preview-url` chunk; without it the bare invoke swallows the
  // chunks and the preview overlay hangs on its placeholder forever (#1932).
  'dev:start',
  'preview:restart',
  'interview:start-building',
  // #2257 — the plan-edit siblings of start-building. They were already
  // reaching the wire (not stubbed), so the cloud bug was a missing server
  // method, now registered in `jsonrpc-methods.ts` / `design-methods.ts`.
  // Listing them here keeps them in lockstep with start-building and arms the
  // object-params guard so they can never silently fall back to a stub.
  'interview:rebuild-from-plan',
  'interview:back-to-plan',
  'interview:save-plan',
  'design:openProject',
  'design:writeProjectMeta',
  'design:readSection',
  'design:writeSection',
  'design:addSection',
  'design:deleteSection',
  'design:listScreens',
  'design:readScreen',
  'design:writeScreen',
  'design:deleteScreen',
  'design:readGraph',
  'design:writeGraph',
  'design:listReferences',
  'design:readReference',
  'design:listLenses',
  'design:readLens',
  'design:listReferenceDocs',
  'design:readReferenceDoc',
  'design:aiChat',
  'design:aiStreamStart',
  'design:aiStreamCancel',
  'design:exportFoundryPackage',
  'design:getBuildUpdateStatus',
  'design:commitBuildSnapshot',
  'design:cancelExport',
  'design:getReconcileStatus',
  'design:reconcileFromChat',
  'design:applyReconcile',
  'build:autoPipeline:cancel',
  'build:autoPipeline:retry',
  // AskUserQuestion (cloud): the SPA answers the agent's question by
  // invoking this; the runtime resolves the pod-side gate so the SDK's
  // canUseTool returns the answer. The matching `agent:ask-user-question`
  // PUSH routes back via the notification demux (no set membership).
  'agent:answer-user-question',
] as const);

export interface CookieBootDependencies {
  /**
   * Probe `/crucible/me` to determine signed-in state. The default
   * factory wires `globalThis.fetch`; tests inject a recorder.
   */
  probe: () => Promise<ProbeResult>;
  /**
   * Construct the browser client. Tests inject a stub that records
   * connect() calls without opening a real socket.
   */
  newClient: (opts: BrowserCrucibleClientOptions) => BrowserCrucibleClient;
  /**
   * Register a `pagehide` listener with the window. Returns an
   * unregister fn.
   */
  onPageHide: (handler: (event: PageTransitionEvent) => void) => () => void;
  /** Same shape for pageshow. */
  onPageShow: (handler: (event: PageTransitionEvent) => void) => () => void;
  /**
   * Install the `window.crucible` shim synchronously, BEFORE the
   * boot transitions to 'ready' (codex round-1 S1 from spa-boot.ts).
   * Returns an `uninstall` fn the boot effect's cleanup calls.
   * Tests inject a recorder.
   */
  installShim: (client: BrowserCrucibleClient) => () => void;
  /**
   * Register a cross-tab logout listener. The default impl wires
   * `BroadcastChannel('foundry-auth')` and invokes the handler when
   * `{kind:'auth:cleared'}` arrives (sent by `logout.ts:beginLogout`
   * from another tab). Returns an unregister fn.
   *
   * Best-effort: the default impl catches the BroadcastChannel
   * constructor throw so a legacy browser without support degrades
   * silently — passive tabs would then transition to signed-out via
   * the next /crucible/me 401 instead of immediately, which is the
   * pre-cross-tab-sync behaviour.
   */
  onAuthCleared: (handler: () => void) => () => void;
}

let cachedCookieBootDefaults: CookieBootDependencies | null = null;

/** Reset the module-scope cache. Test-only. */
export function __resetDefaultCookieBootDependenciesForTests(): void {
  cachedCookieBootDefaults = null;
}

export function defaultCookieBootDependencies(): CookieBootDependencies {
  if (cachedCookieBootDefaults) return cachedCookieBootDefaults;
  const probeDeps: ProbeDependencies = defaultProbeDependencies();
  cachedCookieBootDefaults = {
    probe: () => probeIdentity(probeDeps),
    newClient: (opts) => new BrowserCrucibleClient(opts),
    onPageHide: (handler) => {
      const wrapped = (e: Event): void => handler(e as PageTransitionEvent);
      window.addEventListener('pagehide', wrapped);
      return () => window.removeEventListener('pagehide', wrapped);
    },
    onPageShow: (handler) => {
      const wrapped = (e: Event): void => handler(e as PageTransitionEvent);
      window.addEventListener('pageshow', wrapped);
      return () => window.removeEventListener('pageshow', wrapped);
    },
    installShim: (client) => installWindowCrucibleShim(client),
    onAuthCleared: (handler) => {
      let channel: BroadcastChannel;
      try {
        channel = new BroadcastChannel(AUTH_BROADCAST_CHANNEL);
      } catch {
        // Legacy browser without BroadcastChannel — registering is a
        // no-op; passive-tab degradation happens via natural 401.
        return () => undefined;
      }
      const wrapped = (event: MessageEvent<unknown>): void => {
        const data = event.data as AuthClearedMessage | null | undefined;
        if (data?.kind === 'auth:cleared') handler();
      };
      channel.addEventListener('message', wrapped);
      return () => {
        channel.removeEventListener('message', wrapped);
        channel.close();
      };
    },
  };
  return cachedCookieBootDefaults;
}

export interface BootCancelToken {
  cancelled: boolean;
}

interface DepsRefShape {
  current: CookieBootDependencies;
}

interface ClientRefShape {
  current: BrowserCrucibleClient | null;
}

interface ShimUninstallRefShape {
  current: (() => void) | null;
}

type SetCookieBootState = (
  next: CookieBootState | ((s: CookieBootState) => CookieBootState),
) => void;

/**
 * Extracted boot logic — unit tests drive this directly without
 * needing a DOM / useEffect runner (mirrors `spa-boot.ts:runBoot`).
 *
 * Every await checks `cancelToken.cancelled` after resolving;
 * post-cancel paths fire teardown (`client.close()`) but skip the
 * setState.
 */
export async function runCookieBoot(
  depsRef: DepsRefShape,
  cancelToken: BootCancelToken,
  setState: SetCookieBootState,
  clientRef: ClientRefShape,
  shimUninstallRef: ShimUninstallRefShape,
): Promise<void> {
  if (!cancelToken.cancelled) {
    setState((s) => ({ ...s, phase: 'probing' }));
  }

  // 1. Probe /crucible/me.
  const probeResult = await depsRef.current.probe();
  if (cancelToken.cancelled) return;

  if (probeResult.kind === 'signed-out') {
    setState({
      phase: 'signed-out',
      client: null,
      capabilities: null,
      user: null,
    });
    return;
  }
  if (probeResult.kind === 'error') {
    setState({
      phase: 'connect-error',
      client: null,
      capabilities: null,
      user: null,
      errorReason: probeResult.reason,
    });
    return;
  }

  const user: CookieBootUser = {
    userId: probeResult.userId,
    authMode: probeResult.authMode,
  };

  // 2. Construct + connect the cookie-mode client.
  const client = depsRef.current.newClient({
    // tenantHost omitted → uses window.location.host
    // getAccessToken omitted → no JWT subprotocol, cookie carries identity
    capPrefix: CRUCIBLE_CAP_PREFIX,
    // #2910 — announce the real SPA build version on session.hello@1
    // (vite define; see vite.config.ts). Guarded so vitest/jsdom runs
    // without the define fall back to the legacy dev literal.
    clientVersion:
      typeof __CRUCIBLE_WEB_VERSION__ === 'string'
        ? `crucible-web@${__CRUCIBLE_WEB_VERSION__}`
        : 'browser-spa-dev',
    clientSurface: 'web-cloud',
    // INJECT the app-specific Electron-only stub table (TDD §3.5 / §5.2
    // step 5). The SDK core (@volley/crucible-client) is stub-agnostic;
    // the cookie-mode SPA supplies the canonical table so Electron-only
    // channels (auto-updater, VWR pairing, local-FS, credential store,
    // mount-time config/feature-flag reads, etc.) keep returning their
    // typed browser-stub sentinels instead of round-tripping to a cap
    // host that has no handler.
    stubChannels: ELECTRON_ONLY_STUBS,
    // Lane A 1a: route graduated channels (app:get-feature-flags) to
    // the runtime instead of the injected stub so the SPA sees the pod's
    // real cloudVmV1 + per-user cohort flags.
    runtimeServedChannels: COOKIE_MODE_RUNTIME_SERVED_CHANNELS,
    // BG-4.1 / gui-parity-plan.md GAP-4: replay any sessionId stashed by
    // a prior boot (this tab) so the client can drive `session.resume@1`
    // instead of always minting a fresh session via `session.hello@1`.
    // Pre-BG-4.1 this was omitted, so every boot started session-less and
    // the GUI structurally could never resume. Read once at construct;
    // null → first connect mints fresh (unchanged behaviour). Scope =
    // sessionStorage, global per tab (see session-id-store.ts for the
    // deliberate scope rationale). `?? undefined` because the option is
    // `sessionId?: string`, not `string | null`.
    sessionId: readStoredSessionId() ?? undefined,
    // BG-4.1 / gui-parity-plan.md GAP-4: persist the minted/confirmed
    // sessionId (and clear it on `''` after a hard session loss) so the
    // next boot of this tab reuses it. This closes the verbatim no-op
    // TODO that GAP-4 flagged. Persistence only — the actual
    // `session.resume@1` wiring (re-subscribe in-flight `agent:stream`
    // with `lastSeenSeq` replay) is Phase 4 and lives behind the client's
    // existing `sessionId`/`sendResume` seam (BrowserCrucibleClient
    // ~L2087); nothing further to wire here.
    onSessionId: persistSessionId,
  });
  clientRef.current = client;

  try {
    await client.connect();
    if (cancelToken.cancelled) {
      await client.close();
      return;
    }
    // Install the window.crucible shim SYNCHRONOUSLY before the
    // setState that flips phase to 'ready' (codex round-1 S1 from
    // spa-boot.ts — React's child-first effect ordering means a
    // useEffect-based install would fire AFTER the App tree's
    // children mounted, and those children's mount effects crash on
    // a missing window.crucible).
    shimUninstallRef.current = depsRef.current.installShim(client);
    setState({
      phase: 'ready',
      client,
      capabilities: client.capabilities,
      user,
    });
  } catch (err) {
    if (cancelToken.cancelled) return;
    setState({
      phase: 'connect-error',
      client: null,
      capabilities: null,
      user,
      errorReason: `transport connect failed: ${(err as Error).message}`,
    });
  }
}

/**
 * React hook driving the cookie-mode boot. Mounted once at the SPA
 * root from `main.tsx`.
 *
 * Stability invariants:
 *   - boot useEffect dep array is empty; deps read through a ref
 *     that updates on every render so a fresh `deps` object never
 *     tears down the boot
 *   - bfcache useEffect dep array is empty too — listeners are
 *     registered ONCE at mount, never unregistered between renders
 */
export function useCookieBoot(
  deps: CookieBootDependencies = defaultCookieBootDependencies(),
): CookieBootState {
  const [state, setState] = useState<CookieBootState>({
    phase: 'idle',
    client: null,
    capabilities: null,
    user: null,
  });

  const clientRef = useRef<BrowserCrucibleClient | null>(null);
  const shimUninstallRef = useRef<(() => void) | null>(null);

  // The boot's cancel token, published to a ref so the cross-tab
  // logout listener (a SEPARATE effect) can cancel an in-progress
  // boot. Without this, a broadcast arriving while runCookieBoot is
  // awaiting probe()/connect() would set state to 'signed-out', but
  // the boot would resume after the await and overwrite back to
  // 'ready' — the exact zombie window the feature prevents (Bugbot
  // HIGH on #1256). The boot effect resets `.current` to a FRESH
  // token on every run (StrictMode double-invoke safety): a token
  // initialised once at the ref would carry a stale cancelled=true
  // from the first invocation's cleanup into the second run's boot.
  const cancelTokenRef = useRef<BootCancelToken>({ cancelled: false });

  const depsRef = useRef(deps);
  depsRef.current = deps;

  useEffect(() => {
    const cancelToken = { cancelled: false };
    cancelTokenRef.current = cancelToken;
    void runCookieBoot(depsRef, cancelToken, setState, clientRef, shimUninstallRef);
    return () => {
      cancelToken.cancelled = true;
      shimUninstallRef.current?.();
      shimUninstallRef.current = null;
      clientRef.current?.close().catch(() => undefined);
      clientRef.current = null;
    };
  }, []);

  // Cross-tab logout sync. Another tab's `beginLogout` posts
  // `{kind:'auth:cleared'}` on BroadcastChannel('foundry-auth')
  // immediately BEFORE its top-level redirect, so passive tabs hear
  // about the sign-out before their next request to the runtime
  // fails 401. On receive: tear down any live client + flip phase
  // to 'signed-out'. Safe to fire from any current phase — already-
  // signed-out tabs see a no-op-shaped transition; not-yet-booted
  // tabs get pre-empted to the same terminal state the natural 401
  // would have produced.
  //
  // Cancels the in-progress boot FIRST (cancelTokenRef): if the
  // broadcast lands mid-boot, runCookieBoot's post-await
  // `cancelToken.cancelled` checks short-circuit before it can
  // overwrite state back to 'ready'. There is no in-SPA re-auth
  // path in cookie-mode — signing back in is a top-level nav to
  // /login that remounts the SPA with a fresh token — so a
  // permanently-cancelled boot for this mount is correct.
  useEffect(() => {
    const unregister = depsRef.current.onAuthCleared(() => {
      cancelTokenRef.current.cancelled = true;
      shimUninstallRef.current?.();
      shimUninstallRef.current = null;
      clientRef.current?.close().catch(() => undefined);
      clientRef.current = null;
      setState({
        phase: 'signed-out',
        client: null,
        capabilities: null,
        user: null,
      });
    });
    return () => unregister();
  }, []);

  // bfcache wiring — register pagehide / pageshow once on mount.
  // Mirrors `useSpaBoot`'s pattern. Reading via depsRef.current keeps
  // the dep array flat.
  useEffect(() => {
    const unregisterHide = depsRef.current.onPageHide((event) => {
      if (!event.persisted) return;
      // Bfcache eviction in progress. Drain in-flight requests with
      // a retryable error and clear the resumption tuple so the next
      // pageshow boots a fresh session.hello@1.
      clientRef.current?.drainForBfcache();
    });
    const unregisterShow = depsRef.current.onPageShow((event) => {
      if (!event.persisted) return;
      // Restore from bfcache. The drain above zeroed the sessionId
      // so a fresh connect is safe. Re-attempt the client connect.
      const client = clientRef.current;
      if (!client) return;
      client.connect().catch(() => {
        setState((s) => ({
          ...s,
          phase: 'connect-error',
          errorReason: 'bfcache restore: reconnect failed',
        }));
      });
    });
    return () => {
      unregisterHide();
      unregisterShow();
    };
  }, []);

  return state;
}
